import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import queryString from 'query-string'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import styled from 'styled-components'

import { ALL_COMPANIES_OPTION } from 'utils/partnerUtils'

import {
  selectCompanySelectorObject,
  selectPartner,
  selectSelfCompanyId,
  selectSortedCompaniesOptions,
} from 'containers/App/selectors'
import { applyCompanyFilter, companyFilterChange } from 'containers/PartnerOptions/actions'
import { selectCompanyFilter } from 'containers/PartnerOptions/selectors'
import {
  isIncidentsPage,
  syncCompanyFilterToUrl,
} from 'containers/PartnerOptions/syncCompanyFilterToUrl'

import SearchInput from 'components/lib/DesignSystem/V3/SearchInput'
import Tooltip from 'components/lib/DesignSystem/V3/Tooltip'

import {
  useAllCompaniesSupported,
  useCompanyScopeSupported,
  useFallbackOnUnsupportedRoute,
} from './useAllCompaniesPageScope'

// ---------------------------------------------------------------------------
// Chevron icon
// ---------------------------------------------------------------------------

const ChevronIcon = ({ open }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    style={{
      transition: 'transform 0.15s ease',
      transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
    }}
  >
    <path
      d="M6 9l6 6 6-6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const Wrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  max-width: 280px;
  min-width: 0;
`

// Transparent pill on the bar (Figma rest trigger 1:30913 / hover 4:43980):
// no fill at rest, rail-blue hover, 12px inset, 7px label→icon gap. Colors
// come from the --top-navigation-* tokens TopBar re-points per theme (the
// Figma frames only spec raw dark-theme values).
const Trigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: transparent;
  color: var(--top-navigation-text);
  font-family: Poppins, sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 260px;
  min-width: 0;
  transition:
    border-color 0.15s ease,
    background-color 0.15s ease;
  user-select: none;

  &:hover {
    background: var(--sidebar-item-hover-background);
  }

  &:focus-visible {
    outline: 2px solid var(--color-border);
    outline-offset: 2px;
  }

  &[aria-expanded='true'] {
    background: var(--sidebar-item-hover-background);
  }

  /* Open field matches the dark combobox (Figma 4:56044): same card fill and
     stroke as the list, not the rail's faint hover wash. */
  html[data-theme='dark'] &[aria-expanded='true'] {
    background: var(--color-surface-foreground);
    border-color: var(--color-border);
  }

  /* Pages that don't honor the company filter (IC-48828): greyed + inert,
     tooltip explains why. aria-disabled (not native disabled) so the wrapping
     Tooltip still receives hover/focus. */
  &[aria-disabled='true'] {
    opacity: 0.5;
    cursor: not-allowed;

    &:hover {
      background: transparent;
    }
  }
`

const TriggerLabel = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

// Panel geometry per the Figma Combobox (4:56044 dark / 4:51742 light): 200px
// wide, radius 8, 4px inner inset, 4px below the trigger, no dividers. The
// search field is NOT in the Figma combobox but IS required by Confluence
// 1888223235 §UI Behavior ("A search field (Search companies…) that filters
// the list as the user types") — Confluence governs behavior, so it stays.
// Border + surface stay theme-aware. The shadow differs per theme:
//   - light : soft brand-tinted drop shadow (Figma box-shadow/primary/md)
//   - dark  : deep black drop shadow + 1px white top inner highlight (Card-Dark)
//
// Anchored RIGHT, matching NotificationsMenu / ProfileMenu (IC-50763): the
// trigger sits in the bar's right-edge action cluster and its width tracks the
// company name, so a left-anchored fixed-width panel overhangs the viewport for
// short names (<=5 chars) — clipping the panel and giving the document a
// horizontal scrollbar that shifts the whole page left.
const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: var(--z-index-navigation, 1000);
  background: var(--color-elevated-solid);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  box-shadow:
    0px 2px 4px -2px rgba(48, 99, 253, 0.2),
    0px 4px 6px -1px rgba(48, 99, 253, 0.2);
  width: 200px;
  overflow: hidden;

  html[data-theme='dark'] & {
    /* Figma 4:56044 "Stat in Card BG - Dark": 50% black over #151b56.
       --color-surface-foreground is that fill. Stroke is --color-border
       (rgba(148, 163, 184, 0.2)). */
    background: var(--color-surface-foreground);
    box-shadow:
      0px 8px 32px 0px rgba(0, 0, 0, 0.3),
      0px 4px 16px 0px rgba(0, 0, 0, 0.2),
      inset 0px 1px 0px 0px rgba(255, 255, 255, 0.1);
  }
`

const SearchBox = styled.div`
  padding: 4px 4px 0;
`

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 4px;
  max-height: 280px;
  overflow-y: auto;
  overflow-x: hidden;

  /* Thin scrollbar */
  scrollbar-width: thin;
  scrollbar-color: var(--color-border) transparent;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--color-border);
    border-radius: 2px;
  }
`

// Rows per the Figma combobox: 6px/8px inset, radius 6, long company names
// wrap to extra lines (the mock's selected row wraps to two).
//
// Selected fill is an [aria-selected] selector, not a prop-gated css block.
// `html[data-theme='dark'] &` inside a conditional block is attached to every
// Item, so in dark mode the whole list painted as selected (Figma 4:56044
// dark / 4:51742 light — one row only):
//   - light : pale-blue fill (--ds-blue-7) + navy text, brand-tinted shadow
//   - dark  : solid brand-blue fill (--primary) + white text
const Item = styled.li`
  display: block;
  padding: 6px 8px;
  border-radius: 6px;
  font-family: Poppins, sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: var(--color-text);
  cursor: pointer;
  overflow-wrap: break-word;
  transition:
    background-color 0.1s ease,
    color 0.1s ease;

  &[aria-selected='true'] {
    background: var(--ds-blue-7);
    color: var(--color-text);
    font-weight: 500;
    box-shadow: 0px 1px 2px 0px rgba(48, 99, 253, 0.2);
  }

  html[data-theme='dark'] &[aria-selected='true'] {
    background: var(--primary);
    color: var(--top-navigation-toggle-text);
    box-shadow: none;
  }

  /* Hover is a translucent overlay, suppressed on the selected row so it
     doesn't wash out the solid selected fill. Light uses the shared dropdown
     hover; dark uses the Figma 4:56044 white 20% overlay. */
  &:hover:not([aria-disabled='true']):not([aria-selected='true']) {
    background: var(--top-navigation-dropdown-item-hover-bg);
  }

  html[data-theme='dark'] &:hover:not([aria-disabled='true']):not([aria-selected='true']) {
    background: rgba(255, 255, 255, 0.2);
  }

  &[aria-disabled='true'] {
    opacity: 0.45;
    cursor: not-allowed;
  }
`

const EmptyState = styled.li`
  padding: 12px 14px;
  font-family: Poppins, sans-serif;
  font-size: 13px;
  color: var(--sub-text-color);
  text-align: center;
`

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const TopBarCompanySelector = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const selfCompanyId = useSelector(selectSelfCompanyId)

  const companyFilter = useSelector(selectCompanyFilter)
  const companySelector = useSelector(selectCompanySelectorObject)
  const companies = useSelector(selectSortedCompaniesOptions)
  const allCompaniesSupported = useAllCompaniesSupported()
  // On pages whose data isn't company-scoped, the selector is shown disabled
  // with an explanatory tooltip (IC-48828 product decision).
  const companyScopeSupported = useCompanyScopeSupported()

  // Seed the companyFilter if it is empty / All Companies on mount,
  // reacting to route changes via the fallback hook.
  useFallbackOnUnsupportedRoute()

  // Apply the default company selector object when companyFilter is unset
  // or set to All Companies on non-supporting pages (mirrors existing
  // elements/CompanySelector behaviour).
  useEffect(() => {
    if (!companyFilter || companyFilter.value === ALL_COMPANIES_OPTION.value) {
      if (!allCompaniesSupported) {
        dispatch(applyCompanyFilter(companySelector))
      }
    }
  }, [companyFilter, companySelector, allCompaniesSupported, dispatch])

  // URL changes load that tenant. Redux changes write the URL.
  const companySyncRef = useRef({ search: null, filter: undefined })
  useEffect(() => {
    const prev = companySyncRef.current
    const first = prev.search === null
    const searchChanged = !first && prev.search !== location.search
    const filterChanged = !first && prev.filter !== companyFilter?.value
    companySyncRef.current = { search: location.search, filter: companyFilter?.value }

    if (isIncidentsPage(location.pathname)) return

    const incomingId = Number(queryString.parse(location.search).companyFilter)
    const incoming = incomingId ? companies.find((c) => c.value === incomingId) : null
    const urlNamesADifferentCompany = incoming && incoming.value !== companyFilter?.value

    if (filterChanged && !searchChanged) {
      syncCompanyFilterToUrl({
        history,
        location,
        companyFilter,
        selfCompanyId,
      })
      return
    }
    if (urlNamesADifferentCompany && !filterChanged) {
      dispatch(companyFilterChange({ value: incoming.value, label: incoming.label }))
      return
    }
    syncCompanyFilterToUrl({
      history,
      location,
      companyFilter,
      selfCompanyId,
      honorIncoming: true,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, companies, companyFilter?.value, selfCompanyId, dispatch])

  const [open, setOpen] = useState(false)
  // Search query per Confluence 1888223235 §UI Behavior — filters the company
  // list as the user types. Reset on every open so a stale filter never hides
  // the roster on the next visit.
  const [query, setQuery] = useState('')
  const wrapperRef = useRef(null)

  // Focus the search field as soon as the panel opens.
  useEffect(() => {
    if (!open) return
    wrapperRef.current?.querySelector('input')?.focus()
  }, [open])

  // Close on outside click
  useEffect(() => {
    if (!open) return undefined
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  // Close on Escape
  useEffect(() => {
    if (!open) return undefined
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false)
      }
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [open])

  const handleToggle = useCallback(() => {
    if (!companyScopeSupported) return
    setOpen((prev) => {
      if (!prev) setQuery('')
      return !prev
    })
  }, [companyScopeSupported])

  const handleSelect = useCallback(
    (option) => {
      const nextFilter = { value: option.value, label: option.label }
      dispatch(companyFilterChange(nextFilter))
      syncCompanyFilterToUrl({ history, location, companyFilter: nextFilter, selfCompanyId })
      setOpen(false)
    },
    [dispatch, history, location, selfCompanyId]
  )

  // Current display label
  const isAllCompaniesSelected =
    !companyFilter || companyFilter.value === ALL_COMPANIES_OPTION.value
  const currentLabel = isAllCompaniesSelected
    ? ALL_COMPANIES_OPTION.label
    : (companyFilter?.label ?? companySelector?.label ?? '')

  // Pin the selected company to the top of the list (Confluence 1888223235:
  // "The currently selected company is visually highlighted at the top of the
  // list").
  const orderedCompanies = useMemo(() => {
    if (isAllCompaniesSelected) return companies
    const selected = companies.find((c) => c.value === companyFilter.value)
    if (!selected) return companies
    return [selected, ...companies.filter((c) => c.value !== companyFilter.value)]
  }, [companies, companyFilter, isAllCompaniesSelected])

  // Case-insensitive substring filter over company labels. The All Companies
  // row is a scope option, not a company, so it is never filtered out.
  const visibleCompanies = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return orderedCompanies
    return orderedCompanies.filter((c) => String(c.label).toLowerCase().includes(q))
  }, [orderedCompanies, query])

  // Is the given option the currently selected one?
  const isSelected = useCallback(
    (option) => {
      if (isAllCompaniesSelected) return option.value === ALL_COMPANIES_OPTION.value
      return companyFilter?.value === option.value
    },
    [companyFilter, isAllCompaniesSelected]
  )

  // Wrapped with a tooltip when the page doesn't support All Companies
  // (Confluence 1888223235: greyed-out + explanatory tooltip). Placement:
  // last row per the Figma Combobox (4:56044) — unless it IS the current
  // selection, in which case it renders highlighted at the top instead.
  const allCompaniesRow = (
    <Item
      key="all-companies"
      id="top-bar-company-option-all"
      role="option"
      aria-selected={isAllCompaniesSelected}
      aria-disabled={!allCompaniesSupported}
      onClick={allCompaniesSupported ? () => handleSelect(ALL_COMPANIES_OPTION) : undefined}
    >
      {ALL_COMPANIES_OPTION.label}
    </Item>
  )
  const allCompaniesItem = allCompaniesSupported ? (
    allCompaniesRow
  ) : (
    <Tooltip
      key="all-companies-tooltip"
      content="All Companies is not available on this page"
      side="right"
      delay={200}
    >
      {allCompaniesRow}
    </Tooltip>
  )

  const trigger = (
    <Trigger
      id="top-bar-company-selector-trigger"
      type="button"
      aria-haspopup="listbox"
      aria-expanded={companyScopeSupported ? open : undefined}
      aria-disabled={!companyScopeSupported || undefined}
      onClick={handleToggle}
    >
      <TriggerLabel>{currentLabel}</TriggerLabel>
      <ChevronIcon open={open} />
    </Trigger>
  )

  return (
    <Wrapper ref={wrapperRef}>
      {companyScopeSupported ? (
        trigger
      ) : (
        // Copy per Confluence 1888223235 §Pages That Don't Support Company Switching
        <Tooltip content="Company switching isn't available on this page" side="bottom" delay={200}>
          {trigger}
        </Tooltip>
      )}

      {open && companyScopeSupported && (
        <Dropdown role="dialog" aria-label="Select company">
          <SearchBox>
            <SearchInput
              id="top-bar-company-search"
              size="xs"
              fullWidth
              placeholder="Search companies…"
              aria-label="Search companies"
              value={query}
              onChange={setQuery}
            />
          </SearchBox>
          <List role="listbox" aria-label="Companies">
            {/* All Companies — top row only when it is the current selection */}
            {isAllCompaniesSelected && allCompaniesItem}

            {visibleCompanies.map((company) => (
              <Item
                key={company.value}
                id={`top-bar-company-option-${company.value}`}
                role="option"
                aria-selected={isSelected(company)}
                onClick={() => handleSelect(company)}
              >
                {company.label}
              </Item>
            ))}

            {/* All Companies — last row otherwise (Figma Combobox 4:56044) */}
            {!isAllCompaniesSelected && allCompaniesItem}

            {companies.length === 0 && <EmptyState>No companies available</EmptyState>}
            {companies.length > 0 && visibleCompanies.length === 0 && (
              <EmptyState>No matching companies</EmptyState>
            )}
          </List>
        </Dropdown>
      )}
    </Wrapper>
  )
}

/**
 * Guard — only renders when inside a partner (MSP) context.
 * Mirrors the CompanySelectorGuard in elements/CompanySelector.js.
 */
const TopBarCompanySelectorGuard = () => {
  const partner = useSelector(selectPartner)
  return partner ? <TopBarCompanySelector /> : null
}

export default TopBarCompanySelectorGuard
